import { z } from "zod";
import {
  codeToolSecretNameSchema
} from "../../helpers/codeToolTurn.js";
import { CallDirection, callDirectionSchema } from "../callDirection.js";
import { ianaTimeZoneSchema } from "../company.js";
import {
  baseToolPatchPayloadSchema,
  baseToolPostPayloadSchema
} from "./base.js";
import { customToolMockSchema, customToolParameterSchema } from "./custom.js";
import { webDocumentResearchToolAllowedDomainSchema } from "./webDocumentResearch.js";
const CodeToolType = "code";
const defaultCodeToolTimeoutMs = 1e4;
const codeToolMaxCodeLength = 1e5;
const codeToolMaxTimeoutMs = 3e4;
const codeToolMaxAllowedDomainCount = 20;
const codeToolMaxSecretCount = 20;
const codeToolSecretValueMinLength = 4;
const codeToolSecretValueMaxLength = 4096;
const codeToolTooManySecretsMessage = `A tool can have no more than ${codeToolMaxSecretCount.toString()} secrets.`;
const codeToolAllowedDomainListSchema = z.array(webDocumentResearchToolAllowedDomainSchema).max(
  codeToolMaxAllowedDomainCount,
  `Add no more than ${codeToolMaxAllowedDomainCount.toString()} allowed domains.`
);
function dedupeDomains(domains) {
  return Array.from(new Set(domains));
}
const codeToolSettingsSchema = z.object({
  code: z.string().nonempty().max(codeToolMaxCodeLength),
  parameters: z.array(customToolParameterSchema).optional(),
  /** The hostnames the script may send requests to, matched exactly. */
  allowed_domains: codeToolAllowedDomainListSchema.transform(dedupeDomains),
  timeout_ms: z.int().positive().max(codeToolMaxTimeoutMs).optional(),
  /**
   * Mock configuration for simulations.
   * If not provided, a default simulation message will be used.
   */
  mock: customToolMockSchema.optional()
});
const codeToolSecretWriteSchema = z.object({
  value: z.string().min(codeToolSecretValueMinLength).max(codeToolSecretValueMaxLength),
  allowed_domains: codeToolAllowedDomainListSchema.min(1, "Add at least one domain the secret may be sent to.").transform(dedupeDomains)
});
const codeToolSecretsWriteSchema = z.record(codeToolSecretNameSchema, codeToolSecretWriteSchema.nullable()).refine((secrets) => Object.keys(secrets).length <= codeToolMaxSecretCount, {
  message: codeToolTooManySecretsMessage
});
function describeCodeToolSecretDomainsOutsideTool(secrets, toolAllowedDomains) {
  const toolDomains = new Set(toolAllowedDomains);
  const problems = [];
  for (const [name, secret] of Object.entries(secrets)) {
    const outside = (secret?.allowed_domains ?? []).filter(
      (domain) => !toolDomains.has(domain)
    );
    if (outside.length > 0) {
      problems.push(`${name} (${outside.join(", ")})`);
    }
  }
  return problems.length === 0 ? void 0 : `Secrets can only be sent to the tool's allowed domains: ${problems.join("; ")}.`;
}
const codeToolInputCustomFieldsSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean()])
);
const codeToolInputContactSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  phone_number: z.string().nullable(),
  ext_id: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  custom_fields: codeToolInputCustomFieldsSchema
});
const codeToolInputCallSchema = z.object({
  direction: callDirectionSchema,
  user_phone_number: z.string().nullable(),
  ext_id: z.string().optional()
});
const codeToolInputCallTaskSchema = z.object({
  id: z.string(),
  ext_id: z.string().optional()
});
const codeToolInputContextSchema = z.object({
  customer_interaction: z.object({
    id: z.string(),
    custom_fields: codeToolInputCustomFieldsSchema
  }),
  contact: codeToolInputContactSchema,
  call: codeToolInputCallSchema.optional(),
  call_task: codeToolInputCallTaskSchema.optional(),
  company_timezone: ianaTimeZoneSchema
});
const codeToolTestContactId = "00000000-0000-4000-8000-000000000001";
const codeToolTestCustomerInteractionId = "00000000-0000-4000-8000-000000000002";
const codeToolTestCallTaskId = "00000000-0000-4000-8000-000000000003";
const codeToolTestUserPhoneNumber = "+15555550100";
function buildTestCodeToolInputContext(companyTimezone) {
  return {
    customer_interaction: {
      id: codeToolTestCustomerInteractionId,
      custom_fields: {}
    },
    contact: {
      id: codeToolTestContactId,
      email: null,
      phone_number: codeToolTestUserPhoneNumber,
      ext_id: null,
      first_name: "Test",
      last_name: "Caller",
      custom_fields: {}
    },
    call: {
      direction: CallDirection.Inbound,
      user_phone_number: codeToolTestUserPhoneNumber
    },
    company_timezone: companyTimezone
  };
}
function checkCodeToolSettings(settings, ctx) {
  const seenNames = /* @__PURE__ */ new Set();
  for (const { name } of settings.parameters ?? []) {
    if (seenNames.has(name)) {
      ctx.addIssue({
        code: "custom",
        message: `"${name}" is used by more than one parameter.`,
        path: ["settings", "parameters"]
      });
      return;
    }
    seenNames.add(name);
  }
}
function checkCodeToolSecretDomains(secrets, settings, ctx) {
  if (!secrets) return;
  const message = describeCodeToolSecretDomainsOutsideTool(
    secrets,
    settings.allowed_domains
  );
  if (message) {
    ctx.addIssue({ code: "custom", message, path: ["secrets"] });
  }
}
const codeToolPostPayloadSchema = baseToolPostPayloadSchema.extend({
  type: z.literal(CodeToolType),
  settings: codeToolSettingsSchema,
  secrets: codeToolSecretsWriteSchema.optional()
}).superRefine((payload, ctx) => {
  checkCodeToolSettings(payload.settings, ctx);
  checkCodeToolSecretDomains(payload.secrets, payload.settings, ctx);
});
const codeToolPatchPayloadSchema = baseToolPatchPayloadSchema.extend({
  type: z.literal(CodeToolType),
  settings: codeToolSettingsSchema.optional(),
  secrets: codeToolSecretsWriteSchema.optional()
}).superRefine((payload, ctx) => {
  if (payload.settings) {
    checkCodeToolSettings(payload.settings, ctx);
    checkCodeToolSecretDomains(payload.secrets, payload.settings, ctx);
  }
});
export {
  CodeToolType,
  buildTestCodeToolInputContext,
  checkCodeToolSettings,
  codeToolInputContextSchema,
  codeToolMaxAllowedDomainCount,
  codeToolMaxCodeLength,
  codeToolMaxSecretCount,
  codeToolMaxTimeoutMs,
  codeToolPatchPayloadSchema,
  codeToolPostPayloadSchema,
  codeToolSecretValueMaxLength,
  codeToolSecretValueMinLength,
  codeToolSecretsWriteSchema,
  codeToolSettingsSchema,
  codeToolTestCallTaskId,
  codeToolTestContactId,
  codeToolTestCustomerInteractionId,
  codeToolTestUserPhoneNumber,
  codeToolTooManySecretsMessage,
  defaultCodeToolTimeoutMs,
  describeCodeToolSecretDomainsOutsideTool
};
//# sourceMappingURL=code.js.map
